import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LibraryBrowser } from "./library-browser";
import type { BrandAsset } from "@/hooks/use-brand-assets";

export function LibraryPickerModal({
  isOpen, onClose, brandId, type = null, selectedUrls = [], multi = true, max, onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  type?: string | null;
  selectedUrls?: string[];
  multi?: boolean;
  max?: number;
  onConfirm: (urls: string[], assets: BrandAsset[]) => void;
}) {
  const { t } = useTranslation("common");
  const [urls, setUrls] = useState<string[]>(selectedUrls);
  const [assets, setAssets] = useState<BrandAsset[]>([]);

  // Reset the working selection each time the modal opens (both urls and the
  // resolved assets, so a stale assets list can't be confirmed).
  useEffect(() => {
    if (isOpen) {
      setUrls(selectedUrls);
      setAssets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("library.chooseTitle")}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <LibraryBrowser
            brandId={brandId}
            type={type}
            selectable
            multi={multi}
            max={max}
            selectedUrls={urls}
            onSelectChange={(u, a) => {
              setUrls(u);
              setAssets(a);
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("library.cancel")}</Button>
          <Button
            disabled={urls.length === 0}
            onClick={() => {
              onConfirm(urls, assets);
              onClose();
            }}
          >
            {t("library.addSelected")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
